import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Measures an element's content height (via scrollHeight) and keeps it updated.
 */
const useElementHeight = <TElement extends HTMLElement>(
    watchKey?: string | number
): { ref: (node: TElement | null) => void; height: number | undefined } => {
    const [element, setElement] = useState<TElement | null>(null);
    const [height, setHeight] = useState<number | undefined>(undefined);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const mutationObserverRef = useRef<MutationObserver | null>(null);

    const ref = useCallback((node: TElement | null) => {
        setElement(node);
    }, []);

    useEffect(() => {
        if (!element) return;

        // Clean up any existing observers first
        if (resizeObserverRef.current) {
            resizeObserverRef.current.disconnect();
            resizeObserverRef.current = null;
        }
        if (mutationObserverRef.current) {
            mutationObserverRef.current.disconnect();
            mutationObserverRef.current = null;
        }

        // Use ResizeObserver if available, otherwise fallback to MutationObserver
        if (typeof ResizeObserver !== "undefined") {
            const updateHeight = () => {
                setHeight(element.scrollHeight);
            };

            // Initial + post-layout measurement to handle fast DOM updates reliably.
            updateHeight();
            requestAnimationFrame(updateHeight);

            resizeObserverRef.current = new ResizeObserver(() => {
                requestAnimationFrame(updateHeight);
            });

            resizeObserverRef.current.observe(element);

            return () => {
                if (resizeObserverRef.current) {
                    resizeObserverRef.current.disconnect();
                    resizeObserverRef.current = null;
                }
            };
        } else if (typeof MutationObserver !== "undefined") {
            const updateHeight = () => {
                setHeight(element.scrollHeight);
            };

            // Initial measurement
            updateHeight();

            mutationObserverRef.current = new MutationObserver(() => {
                // Let DOM mutations flush/layout happen before measuring.
                requestAnimationFrame(updateHeight);
            });

            mutationObserverRef.current.observe(element, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ["style", "class"],
            });

            return () => {
                if (mutationObserverRef.current) {
                    mutationObserverRef.current.disconnect();
                    mutationObserverRef.current = null;
                }
            };
        }

        // If neither observer is available, do nothing
        return undefined;
    }, [element, watchKey]);

    return { ref, height };
};

export default useElementHeight;
